import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import StopOutlinedIcon from '@mui/icons-material/StopOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent
} from '@mui/material';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { PageLoading } from '@bumble/app/components/PageLoading';

interface RouterPacketCaptureProps {
  cpeId: string;
}

export const RouterPacketCapture = ({ cpeId }: RouterPacketCaptureProps) => {
  const api = useApi();
  const [isCapturing, setIsCapturing] = useState<boolean>(false);
  const [buttonClicked, setButtonClicked] = useState<boolean>(false);
  const [interfaces, setInterfaces] = useState<any[]>([{ name: 'Any', role: 'ANY' }]);
  const [currentInterface, setCurrentInterface] = useState('ANY');

  const { isLoading: isLoading1, data: data1 } = useQuery(
    ['getCpeInterfaces', cpeId],
    api.getCpeInterfacesApiV1GetCpeInterfacesGet.bind(null, {
      cpe_id: cpeId,
      hardware_only: true,
    }),
    { enabled: true },
  );

  useEffect(() => {
    data1?.data && setInterfaces([{ name: 'Any', role: 'ANY' }].concat(data1?.data));
  }, [data1]);

  const { isLoading: isLoading2, data: data2 } = useQuery(
    ['getCaptureStatus'],
    api.getCaptureStatusApiV1GetCaptureStatusGet.bind(null, { cpe_id: cpeId }),
    { enabled: true, cacheTime: 0 },
  );

  useEffect(() => {
    setIsCapturing(data2?.data.capturing === true);
  }, [data2]);

  const startCapture = useMutation(api.startCaptureApiV1StartCapturePost, {
    onSuccess: async () => {
      setButtonClicked(false);
      setIsCapturing(true);
    },
    onError: (error) => {
      apiError(error);
      setButtonClicked(false);
    }
  });

  const stopCapture = useMutation(api.stopCaptureApiV1StopCapturePost, {
    onSuccess: async () => {
      setButtonClicked(false);
      setIsCapturing(false);
    },
    onError: (error) => {
      apiError(error);
      setButtonClicked(false);
    }
  });

  const cancelCapture = useMutation(api.cancelCaptureApiV1CancelCapturePost, {
    onSuccess: async () => {
      setButtonClicked(false);
      setIsCapturing(false);
    },
    onError: (error) => {
      apiError(error);
      setButtonClicked(false);
    }
  });

  const handleChange = ({ target: { value }}: SelectChangeEvent): void => {
    setCurrentInterface(interfaces.find(i => i.role === value)?.role);
  };

  const handleStart = () => {
    setButtonClicked(true);
    startCapture.mutate({
      node_id: cpeId,
      ...(currentInterface && { interface: currentInterface })
     });
  }

  const handleCancel = () => {
    setButtonClicked(true);
    cancelCapture.mutate({ cpe_id: cpeId });
  }

  const handleStop = () => {
    setButtonClicked(true);
    stopCapture.mutate({ cpe_id: cpeId });
  }

  return (
    <>
      {(isLoading1 || isLoading2 || buttonClicked) && <PageLoading content="Working on it..." />}
      <Box pt={2}>
        <Box display="flex" justifyContent="center" sx={{ mb: 4 }}>
          <FormControl fullWidth>
            <InputLabel id="reset-LAN-port">Interface</InputLabel>
            <Select
              labelId="reset-LAN-port"
              value={currentInterface}
              onChange={handleChange}
              label="Interface"
            >
              {interfaces && interfaces.map(({name, role}) => {
                return role === 'ANY' ?
                  <MenuItem value={role} key={role}>{role}</MenuItem> :
                  <MenuItem value={role} key={role}>{`${name} (${role})`}</MenuItem>;
              })}
            </Select>
          </FormControl>
        </Box>
        <Box display="flex" justifyContent="center" sx={{ mt: 5 }}>
          {!isCapturing ? (
            <Button startIcon={<PlayArrowOutlinedIcon />} size="large" variant="outlined" onClick={handleStart}>
              Start
            </Button>
          ) : (
            <>
              <Button startIcon={<CloseOutlinedIcon />} size="large" variant="outlined" onClick={handleCancel} sx={{mr: 5}}>
                Cancel
              </Button>
              <Button startIcon={<StopOutlinedIcon />} size="large" variant="outlined" onClick={handleStop}>
                Stop
              </Button>
            </>
          )}
        </Box>
      </Box>
    </>
  );
};
